import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { INTAKE_PURPOSE_OPTIONS, INTAKE_DEFAULT_STATUS_OPTIONS, btn, labelClass, inputClass, card } from "@/components/org/intake/intakeMeta";

export const BasicInfoStep = ({ model, onCreated, onSaved }) => {
  const { API } = useAuth();
  const [values, setValues] = useState({
    name: model?.name || "",
    description: model?.description || "",
    purpose: model?.purpose || "Service Request",
    default_status: model?.default_status || "new",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const save = async () => {
    setError("");
    if (!values.name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (model) {
        const { data } = await axios.patch(`${API}/org/intake/models/${model.model_id}/basic`, values, { withCredentials: true });
        onSaved(data.model);
      } else {
        const { data } = await axios.post(`${API}/org/intake/models`, values, { withCredentials: true });
        onCreated(data.model);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={card} data-testid="wizard-step-basic">
      <p className="text-sm font-bold text-slate-950 dark:text-white">Basic Information</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Name this Intake Model and tell us what it's for. Customers reach it only via a stable public link or QR code — no website embed needed.</p>
      <div className="mt-4 space-y-3">
        <div>
          <label className={labelClass}>Name *</label>
          <input value={values.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Service Request Intake" data-testid="wizard-basic-field-name" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea value={values.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="wizard-basic-field-description" className={inputClass} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Purpose</label>
            <select value={values.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="wizard-basic-field-purpose" className={`${inputClass} bg-white dark:bg-[#0B1220]`}>
              {INTAKE_PURPOSE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Default Status</label>
            <select value={values.default_status} onChange={(e) => set("default_status", e.target.value)} data-testid="wizard-basic-field-default-status" className={`${inputClass} bg-white dark:bg-[#0B1220]`}>
              {INTAKE_DEFAULT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Destination</label>
          <p className="mt-1 text-sm font-semibold text-slate-600 dark:text-slate-300">Front Desk — submissions appear there for manual review &amp; qualification into Sales.</p>
        </div>
      </div>
      {error && <p className="mt-3 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="wizard-basic-error">{error}</p>}
      <button type="button" onClick={save} disabled={saving} data-testid="wizard-basic-save-btn" className={`${btn} mt-5`}>
        {saving && <Loader2 size={14} className="animate-spin" />} {model ? "Save & Continue" : "Create & Continue"}
      </button>
    </div>
  );
};
